import {
  find,
  map,
  get,
  isEmpty,
  forEach,
  includes
} from 'lodash';
import { parsePhoneNumberFromString, AsYouType } from 'libphonenumber-js';
import { MDTAnalytics } from '../services/mdtanalytics.js';
import { AnalyticsConstants } from './analyticsConstants.js';

/**
 * We will assume that a string is translatable when:
 * - Has no spaces on the sentence
 * @param string
 * @returns {boolean}
 */
export const isTranslatableLabel = (string) => {
  return !string.trim().includes(' ');
}

/**
 * @param {Array} userRoles
 * @returns {String} String of assigned user roles to perticular user.
 */
export const getUserRoles = userRoles => {
  const rolesArray = userRoles.length && find(userRoles, k => k.appId === appSettings.appid) || [];
  const roles = rolesArray.roles && rolesArray.roles.length > 0 && map(rolesArray.roles, o => o.name) || '';
  const filteredRoles = Array.isArray(roles) && roles.filter((el, i, a) => i === a.indexOf(el))
  return (filteredRoles && filteredRoles.join()) || '';
};

export const FormatPhoneNumber = (value) => {
  const phoneNumber = parsePhoneNumberFromString(value);
  let formattedNumber = value;
  if(phoneNumber){
    var val = phoneNumber.country === 'US' ? phoneNumber.nationalNumber : value; //Shows phoneNumber with Country Calling Code except for US
    const asYouType = new AsYouType(phoneNumber.country || 'US');
    formattedNumber = asYouType.input(val);
  }
  return formattedNumber;
};

export const getUrlParameter =  (name) => {
  name = name.replace(/[\[]/, '\\[').replace(/[\]]/, '\\]');
  var regex = new RegExp('[\\?&]' + name + '=([^&#]*)');
  var results = regex.exec(location.search);
  return results === null ? '' : decodeURIComponent(results[1].replace(/\+/g, ' '));
};

export const getLabelsForTracking = (data, toFilter, joiner=':') => {
  if (Array.isArray(data) && data.length) {
    return data
      .map(obj =>  get(obj, toFilter, ''))
      .filter(e => e !== '')
      .join(joiner);
  }
  return '';
}

export const getExtendedLabelsForTracking = (data, propOneToFilter, propTwoToFilter, seperator = '|', joiner = ' : ') => {
  if (Array.isArray(data) && data.length) {
    return data
      .map(obj =>  `${get(obj, propOneToFilter, '')} ${seperator} ${get(obj, propTwoToFilter, '')}`)
      .filter(e => e !== '')
      .join(joiner);
  }
  return '';
}

export const getCustomer = userId => {
  const customerStr = localStorage.getItem('customer');
  if(customerStr == null)
    {
      return 'NA'
    }
  const parsedCustomersArr = customerStr.length && JSON.parse(customerStr);
  const customer =
    Array.isArray(parsedCustomersArr)
    && parsedCustomersArr.filter(obj => obj.userid === userId);
  return get(customer, '[0]', {});
};

const getAnalyticsConstantlabel = (obj, type) => {
  try {
    const { pageName, label, name } = obj;
    return pageName
      ? AnalyticsConstants[type][name](pageName, label)
      : AnalyticsConstants[type][name](label);
  } catch (err) {
    console.error(err);
  }
}

const processTrackingLabelString = (obj, type) => {
  return typeof obj === 'object'
    ? getAnalyticsConstantlabel(obj, type)
    : AnalyticsConstants[type][obj];
};

/** @typedef {'eventLabel'|'eventCategory'|'eventName'|'eventValue'|'eventAction'} TranslatableFields */
/**
 * Send tracking data to MDTAnalytics
 * @param param {Object} - Object containing the data to track
 * @param param.translatableFields {Array[TranslatableFields]} - Array of translatable fields
 */
export const sendEventTrackingData = (param) => {
  const {processLabels = true, processActions = true, customInfo = {}, translatableFields = [] } = param;
  const labelObj = get(param, 'eventLabel');
  const actionObj = get(param, 'eventAction');

  const label = processLabels
      ? processTrackingLabelString(labelObj, 'labels')
      : labelObj;
  const action = processActions
      ? processTrackingLabelString(actionObj, 'actions')
      : actionObj;

  const eventInfo = {
    eventName: AnalyticsConstants.names[get(param, 'eventName', 'dataTrack')],
    eventCategory: AnalyticsConstants.categories[get(param, 'eventCategory')] || 'NA',
    eventAction: action || 'NA',
    eventLabel: label || 'NA',
    eventValue: AnalyticsConstants.values[get(param, 'eventValue', 'one')],
    ...customInfo
  };

  if (eventInfo.eventAction === 'NA' || eventInfo.eventLabel === 'NA' || eventInfo.eventCategory === 'NA') {
    console.warn('sendEventTrackingData: The following eventInfo yield a NA value, this should not happen:',
        { eventInfo, param });
    return;
  }

  // NOTE: Only allow to translate values that are part of the reporting fields 20221018:Alevale
  if (!isEmpty(translatableFields)) {
    if (window.next18 && window.next18.getFixedT) {
      // NOTE: We are going to be sending to the Analytics the keys translated to english 20221018:Alevale
      const en = window.next18.getFixedT('en');
      // NOTE: Overwrite the default eventInfo with the translated values of it 20221018:Alevale
      forEach(translatableFields, (keyToTranslate) => {
        if (includes(Object.keys(eventInfo), keyToTranslate)){
          eventInfo[keyToTranslate] = en(get(param, keyToTranslate));
        }
      })
    }
  }

  MDTAnalytics.track({
    eventInfo,
  }, true);
}

/**
* This translates a key to English so Analytics can make aggregations.
* @param key {String} - The key to translate
* @returns {Promise<*>} The English translation of the key.
*/
export const getEnglishTranslation = async (key) => {
  if (window.next18 && window.next18.getFixedT) {
    const en = window.next18.getFixedT('en');
    if (en(key) === key) {
      await window.next18.loadLanguages('en');
      const retryEn = window.next18.getFixedT('en');
      return retryEn(key);
    }
    return en(key);
  }
  return key;
}

export const getUserType = loginId => {
  const isValidEmail = new RegExp(/^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,3})+$/);
  const isMedtronicEmail = new RegExp('@medtronic.com\s*', 'i');

  let isInternalUser = false;

  if ((isValidEmail.test(loginId) && isMedtronicEmail.test(loginId)) || !isValidEmail.test(loginId)) {
    isInternalUser = true;
  }

  return isInternalUser ? 'Internal' : 'External';
}

export const waitFor = (time) => {
  return new Promise((resolve, reject) => {
    setTimeout(() => {
      resolve(`The timer of ${time} ms finished`)
    }, time)
  })
};

export const alertDocument = (supplierInfos) => {
  let alertObj;
  if(supplierInfos && supplierInfos.documents && supplierInfos.documents.length){
  supplierInfos.documents.map((document)=> {
      if(document && document.componentType && document.componentType.key == 'ALERT'){
        alertObj = {
          content: document.content ? document.content : null,
          type: document.mdtType ? document.mdtType : null,
        }
      } else{
        alertObj = {
          content: null,
          type: null,
        }
      }
  });
} else {
  alertObj = {
      content: null,
      type: null,
    }
}
return alertObj;
};

export const numberFormat = (value, language='en-US', decimalDigits=0) => {
  let number
  try {
    number = Number(value).toLocaleString(language.replace('_', '-'), {
      minimumFractionDigits: decimalDigits,
      maximumFractionDigits: decimalDigits,
    })
  } catch (e) {
    number = ''
  }
  return number;
}

export const pushIntoDigitalData = (data) => {
  try {
      window.digitalData.transactionProducts.push(data);
      sessionStorage.setItem('transactions', JSON.stringify(window.digitalData.transactionProducts));

  } catch (err) {
      console.log(err);
  }
}

export const pushIntoDigitalDataTransaction = (data) => {
  try {
      window.digitalData.transaction.push(data);
      sessionStorage.setItem('transaction', JSON.stringify(window.digitalData.transaction));

  } catch (err) {
      console.log(err);
  }
}

export const formatDataUsingSeparator=(data)=>{
      return data.reduce(function(acc,curr){
        for(let key in acc){
          for(let key in curr){
               acc[key]= acc[key] + "|" +curr[key];
             }
          return acc;
        }
      }
    )
}

export const appendScript = (scriptToAppend, async=true) => {
  const script = document.createElement("script");
  script.src = scriptToAppend;
  script.async = async;
  document.body.appendChild(script);
}